import { type ReactNode, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eye, Pencil, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { SettingsDrawerHeader } from '@/components/settings/SettingsDrawerHeader'
import { ListPagination } from '@/components/shared/ListPagination'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { ConfirmDeleteDialog, LoadingOverlay } from '@/components/shared'
import { useListParams } from '@/hooks/useListParams'
import {
  useDocumentTemplates,
  useCreateDocumentTemplate,
  useUpdateDocumentTemplate,
  useDeleteDocumentTemplate,
  useTemplatePlaceholders,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import { settingsApi } from '@/services/settings'
import { getApiErrorMessage } from '@/services/api'
import { formatDate } from '@/lib/format'
import { DOCUMENT_TYPE, type TemplateDocumentType } from '@/lib/documentType'
import { cn } from '@/lib/utils'
import {
  RichTemplateEditor,
  type RichTemplateEditorHandle,
} from '@/components/settings/templates/RichTemplateEditor'
import type { DocumentTemplate, DocumentTemplateRequest } from '@/types'

interface TemplatePanelProps {
  docType: TemplateDocumentType
  title: string
}

interface FormState {
  name: string
  content: string
  is_default: boolean
}

const EMPTY: FormState = { name: '', content: '', is_default: false }
const COLUMN_COUNT = 4

function expandLegacyPartyBlocks(
  content: string,
  starterContent: string,
  docType: TemplateDocumentType
): string {
  if (docType !== DOCUMENT_TYPE.QUOTE && docType !== DOCUMENT_TYPE.INVOICE) {
    return content
  }

  const prefix = docType === DOCUMENT_TYPE.QUOTE ? 'quote' : 'invoice'
  const starter = new DOMParser().parseFromString(starterContent, 'text/html')
  const partyCells = starter.querySelectorAll(
    `table.${prefix}-parties-grid > tbody > tr > td`
  )
  if (partyCells.length !== 2) return content

  const normalizedContent = content.replace(
    /\{\{client_address\}\}\s*\{\{client_postal_code\}\}\s*\{\{client_city\}\}/g,
    '{{client_full_address}}'
  )

  return ['company', 'client'].reduce((html, party, index) => {
    const token = `${prefix}_${party}_block`
    const replacement = partyCells[index].innerHTML
    const standaloneParagraph = new RegExp(
      `<p(?:\\s[^>]*)?>\\s*\\{\\{${token}\\}\\}\\s*<\\/p>`,
      'g'
    )
    const placeholder = new RegExp(`\\{\\{${token}\\}\\}`, 'g')

    return html
      .replace(standaloneParagraph, () => replacement)
      .replace(placeholder, () => replacement)
  }, normalizedContent)
}

const sectionCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const drawerCardClass =
  'rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_1px_4px_rgba(38,45,66,0.05),0_8px_16px_-12px_rgba(38,45,66,0.1)]'

const primaryButtonClass =
  'h-10 rounded-[12px] border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium leading-none text-white shadow-none hover:bg-[#e6511c]'

const drawerClass =
  'tf-right-drawer-short min-h-0 max-h-dvh *:min-w-0 [&>button]:right-6 [&>button]:top-4 [&>button]:rounded-xl [&>button]:bg-[#f4f5f8] [&>button]:p-2 [&>button]:opacity-100 [&>button]:text-[#555b6a]'

const fieldLabelClass = 'text-xs font-medium leading-4 text-[#171a20]'

const fieldInputClass = cn(
  'h-10 rounded-xl border border-transparent bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] placeholder:text-[#6c7285]',
  FORM_CONTROL_FOCUS_CLASS
)

const switchClass =
  'h-6 w-10 data-[state=checked]:bg-[#10b981] data-[state=unchecked]:bg-[#d9dfe8] [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-4 [&>span]:data-[state=unchecked]:translate-x-0.5'

const thClass =
  'h-12 border-b border-[#eef2f6] bg-[#fbfbfc] px-3 py-2 text-[10px] font-medium leading-[1.2] text-[#3D434E]'

const tdClass =
  'h-16 border-b border-[#eef2f6] px-3 py-2 align-middle text-sm text-[#171a20]'

const actionButtonClass =
  'inline-flex size-8 shrink-0 items-center justify-center rounded-lg border border-transparent bg-[#f4f5f8] p-1.5 text-[#272c36] transition-colors group-hover:bg-[#fbfbfc] disabled:cursor-not-allowed disabled:opacity-50'
const accentActionButtonClass =
  'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] group-hover:hover:bg-[#fff1ea]'
const destructiveActionButtonClass =
  'hover:border-[#d63b33] hover:bg-[#fde9e8] hover:text-[#d63b33] group-hover:hover:bg-[#fde9e8]'
function SectionHeading({ children }: { children: string }) {
  return (
    <div className='-ml-6 flex min-w-0 items-center border-l-4 border-[#ff5a1f] pl-6'>
      <h2 className='min-w-0 font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
        {children}
      </h2>
    </div>
  )
}

function CardHeading({ children }: { children: string }) {
  return (
    <div className='border-b border-transparent px-4 pb-2 pt-4'>
      <div className='-ml-4 flex items-center border-l-4 border-[#ff5a1f] pl-4'>
        <h3 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
          {children}
        </h3>
      </div>
    </div>
  )
}

function InlineSectionHeading({ children }: { children: string }) {
  return (
    <div className='-ml-4 flex items-center border-l-4 border-[#ff5a1f] pl-4'>
      <h4 className='font-heading text-sm font-semibold lowercase leading-[1.2] text-[#3d434e] first-letter:uppercase'>
        {children}
      </h4>
    </div>
  )
}

function DefaultBadge() {
  const { t } = useTranslation('settings')
  return (
    <span className='inline-flex items-center rounded-full bg-[#fff1ea] px-2.5 py-1 text-xs font-medium leading-none text-[#ff5a1f]'>
      {t('templates.defaultBadge')}
    </span>
  )
}

function ActionButton({
  label,
  onClick,
  disabled = false,
  destructive = false,
  children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
  children: ReactNode
}) {
  return (
    <TruncatedTextTooltip
      content={label}
      delayDuration={0}
      alwaysShow
      closeOnPointerDown
      className='inline-flex shrink-0'
    >
      <button
        type='button'
        className={cn(
          actionButtonClass,
          destructive ? destructiveActionButtonClass : accentActionButtonClass
        )}
        aria-label={label}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </button>
    </TruncatedTextTooltip>
  )
}

// 2.11.14-2.11.18 — Generic document template editor (quote / invoice / credit
// note / contract / intervention report) keyed by doc_type. The data flow and
// backend contract stay unchanged; only the shell is restyled to match the
// design system list page and right-side editor drawer.
export function TemplatePanel({ docType, title }: TemplatePanelProps) {
  const { t } = useTranslation('settings')
  const { page, setPage, limit, setLimit, query } = useListParams()
  const { data, isLoading, isError } = useDocumentTemplates({
    ...query,
    doc_type: docType,
  })
  const create = useCreateDocumentTemplate()
  const update = useUpdateDocumentTemplate()
  const remove = useDeleteDocumentTemplate()
  const { data: placeholderData } = useTemplatePlaceholders(docType)
  const placeholders = placeholderData?.data

  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<DocumentTemplate | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY)
  const [nameError, setNameError] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [toDelete, setToDelete] = useState<DocumentTemplate | null>(null)
  const editorRef = useRef<RichTemplateEditorHandle>(null)

  const rows = useMemo(() => data?.data ?? [], [data])
  const set = (patch: Partial<FormState>) => setForm(f => ({ ...f, ...patch }))
  const saving = create.isPending || update.isPending
  const contentPlaceholder =
    docType === DOCUMENT_TYPE.INTERVENTION_REPORT
      ? t('templates.placeholders.reportContent')
      : t('templates.placeholders.content')

  const paletteGroups = useMemo(() => {
    const groups: {
      group: string
      tokens: { token: string; label: string }[]
    }[] = []

    for (const placeholder of placeholders?.placeholders ?? []) {
      let group = groups.find(item => item.group === placeholder.group)
      if (!group) {
        group = { group: placeholder.group, tokens: [] }
        groups.push(group)
      }
      group.tokens.push({
        token: placeholder.token,
        label: placeholder.label,
      })
    }

    return groups
  }, [placeholders])

  function openCreate() {
    setEditing(null)
    setForm({ ...EMPTY, content: placeholders?.starter_content ?? '' })
    setNameError(null)
    setFormError(null)
    setOpen(true)
  }

  async function openEdit(template: DocumentTemplate) {
    let starterContent = placeholders?.starter_content ?? ''
    if (!starterContent) {
      try {
        starterContent =
          (await settingsApi.getTemplatePlaceholders(docType)).data
            ?.starter_content ?? ''
      } catch {
        // The saved template remains editable if the starter request fails.
      }
    }

    setEditing(template)
    setForm({
      name: template.name,
      content: expandLegacyPartyBlocks(
        template.content ?? '',
        starterContent,
        docType
      ),
      is_default: template.is_default,
    })
    setNameError(null)
    setFormError(null)
    setOpen(true)
  }

  function insertToken(token: string) {
    editorRef.current?.insertToken(token)
  }

  async function previewContent(content: string) {
    setPreviewing(true)

    try {
      const res = await settingsApi.previewTemplate({
        doc_type: docType,
        content,
      })
      const url = res.data?.url
      if (url) window.open(url, '_blank', 'noopener')
    } catch (error) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(error, t('templates.preview.error')),
      })
    } finally {
      setPreviewing(false)
    }
  }

  function preview() {
    return previewContent(form.content)
  }

  function submit() {
    if (!form.name.trim()) {
      setNameError(t('templates.validation.nameRequired'))
      return
    }

    setFormError(null)

    const body: DocumentTemplateRequest = {
      doc_type: docType,
      name: form.name.trim(),
      content: form.content,
      is_default: form.is_default,
    }

    const onSuccess = () => {
      toast({
        variant: 'success',
        title: editing
          ? t('templates.toast.updated')
          : t('templates.toast.created'),
      })
      setOpen(false)
    }

    const onError = (error: unknown) => setFormError(getApiErrorMessage(error))

    if (editing) {
      update.mutate({ id: editing.id, body }, { onSuccess, onError })
      return
    }

    create.mutate(body, { onSuccess, onError })
  }

  function confirmDelete() {
    if (!toDelete) return

    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: t('templates.toast.deleted') })
        setToDelete(null)
      },
      onError: error =>
        toast({ variant: 'destructive', title: getApiErrorMessage(error) }),
    })
  }

  return (
    <>
      <section className={sectionCardClass}>
        <div className='flex flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between'>
          <SectionHeading>{title}</SectionHeading>

          <div className='shrink-0'>
            <Button className={primaryButtonClass} onClick={openCreate}>
              <Plus className='mr-1.5 h-4 w-4' />
              {t('templates.addTemplate')}
            </Button>
          </div>
        </div>

        <div className='overflow-x-auto px-6'>
          <table className='w-full min-w-[720px] caption-bottom text-sm'>
            <TableHeader>
              <TableRow className='border-0'>
                <TableHead className={thClass}>{t('common.name')}</TableHead>
                <TableHead className={cn(thClass, 'w-[180px] text-center')}>
                  {t('templates.columns.default')}
                </TableHead>
                <TableHead className={cn(thClass, 'w-[180px]')}>
                  {t('templates.columns.lastUpdated')}
                </TableHead>
                <TableHead className={cn(thClass, 'w-[132px] text-right')}>
                  {t('common.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {isLoading &&
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell colSpan={COLUMN_COUNT} className={tdClass}>
                      <Skeleton className='h-8 w-full rounded-xl' />
                    </TableCell>
                  </TableRow>
                ))}

              {isError && !isLoading && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-12 text-center text-destructive'
                    )}
                  >
                    {t('templates.loadError')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={cn(
                      tdClass,
                      'py-12 text-center text-muted-foreground'
                    )}
                  >
                    {t('templates.empty')}
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                !isError &&
                rows.map(template => {
                  return (
                    <TableRow key={template.id} className='group border-0'>
                      <TableCell className={cn(tdClass, 'font-medium')}>
                        {template.name}
                      </TableCell>
                      <TableCell className={cn(tdClass, 'text-center')}>
                        {template.is_default ? (
                          <DefaultBadge />
                        ) : (
                          <span className='text-[#9aa1af]'>-</span>
                        )}
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <span className='whitespace-nowrap text-[#3d434e]'>
                          {formatDate(template.updated_at)}
                        </span>
                      </TableCell>
                      <TableCell className={cn(tdClass)}>
                        <div className='flex items-center justify-end gap-2'>
                          <ActionButton
                            label={t('templates.preview.button')}
                            onClick={() =>
                              previewContent(template.content ?? '')
                            }
                            disabled={previewing}
                          >
                            <Eye className='size-4' />
                          </ActionButton>
                          <ActionButton
                            label={t('common:actions.edit')}
                            onClick={() => void openEdit(template)}
                          >
                            <Pencil className='size-4' />
                          </ActionButton>
                          <ActionButton
                            label={t('common:actions.delete')}
                            onClick={() => setToDelete(template)}
                            destructive
                          >
                            <Trash2 className='size-4' />
                          </ActionButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </table>
        </div>

        <ListPagination
          metadata={data?.metadata}
          page={page}
          pageSize={limit}
          onPageChange={setPage}
          onPageSizeChange={setLimit}
          className='px-6 py-3'
          showSummary={false}
          showShowingRange={false}
        />
      </section>

      <Dialog
        open={open}
        onOpenChange={next => {
          if (!next && saving) return
          setOpen(next)
        }}
      >
        <DialogContent
          side='right'
          showCloseButton={false}
          className={drawerClass}
        >
          <SettingsDrawerHeader
            title={
              editing
                ? t('templates.dialog.editTitle', { name: title })
                : t('templates.dialog.createTitle', { name: title })
            }
            closeDisabled={saving}
            titleClassName='text-base'
          />

          <div className='flex min-h-0 flex-1 flex-col bg-[#f4f5f8]'>
            <div
              key={editing?.id ?? 'create'}
              className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-6'
            >
              <div className={drawerCardClass}>
                <CardHeading>{t('templates.sections.information')}</CardHeading>

                <div className='flex flex-col gap-6 px-4 pb-6 pt-4'>
                  <div className='flex flex-col gap-1'>
                    <Label htmlFor='tpl-name' className={fieldLabelClass}>
                      {t('templates.fields.name')}
                      <span className='text-[#d63b33]'> *</span>
                    </Label>
                    <Input
                      id='tpl-name'
                      value={form.name}
                      placeholder={t('templates.placeholders.name')}
                      onChange={event => {
                        set({ name: event.target.value })
                        setNameError(null)
                      }}
                      className={cn(
                        fieldInputClass,
                        nameError && 'border-destructive'
                      )}
                    />
                    {nameError && (
                      <p className='text-sm text-destructive'>{nameError}</p>
                    )}
                  </div>

                  <div className='border-b border-[#eef2f6] pb-3'>
                    <div className='flex items-center gap-3'>
                      <Label
                        htmlFor='tpl-default'
                        className='flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'
                      >
                        {t('templates.columns.default')}
                      </Label>
                      <Switch
                        id='tpl-default'
                        checked={form.is_default}
                        onCheckedChange={value => set({ is_default: value })}
                        className={switchClass}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={drawerCardClass}>
                <CardHeading>{t('templates.sections.content')}</CardHeading>

                <div className='flex flex-col gap-0 pb-4 pt-4'>
                  <RichTemplateEditor
                    ref={editorRef}
                    value={form.content}
                    onChange={content => set({ content })}
                    placeholder={contentPlaceholder}
                    layoutMode={
                      docType === DOCUMENT_TYPE.QUOTE
                        ? 'quote'
                        : docType === DOCUMENT_TYPE.INVOICE
                          ? 'invoice'
                          : undefined
                    }
                  />

                  {paletteGroups.length > 0 && (
                    <div className='mt-6 border-t border-[#eef2f6] px-4 pt-4'>
                      <InlineSectionHeading>
                        {t('templates.palette.title')}
                      </InlineSectionHeading>

                      <div className='mt-4 flex flex-col gap-4'>
                        {paletteGroups.map(group => (
                          <div
                            key={group.group}
                            className='flex flex-col gap-2'
                          >
                            <span className='text-xs font-medium text-[#171a20]'>
                              {t(
                                `templates.palette.groups.${group.group}`,
                                group.group
                              )}
                            </span>

                            <div className='flex flex-wrap gap-2'>
                              {group.tokens.map(token => (
                                <button
                                  key={token.token}
                                  type='button'
                                  title={`{{${token.token}}}`}
                                  onClick={() => insertToken(token.token)}
                                  className='rounded-xl border border-[#e7e9ef] bg-white px-3 py-2 text-xs font-normal leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors hover:bg-[#fff8f4] hover:text-[#d9480f]'
                                >
                                  {t(
                                    `templates.tokens.${token.token}`,
                                    token.label
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {formError && (
                <p className='text-sm text-destructive'>{formError}</p>
              )}
            </div>

            <div className='tf-right-drawer-footer justify-between'>
              <Button
                type='button'
                variant='outline'
                className='rounded-xl border-[#e7e9ef] bg-white px-4 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'
                onClick={() => setOpen(false)}
                disabled={saving}
              >
                {t('common:actions.cancel')}
              </Button>

              <div className='flex items-center gap-3'>
                <Button
                  type='button'
                  variant='outline'
                  className='rounded-xl border-[#e7e9ef] bg-white px-4 py-3 text-sm font-medium leading-none text-[#171a20] shadow-none hover:bg-[#f8f9fb]'
                  onClick={preview}
                  disabled={previewing}
                >
                  <Eye className='mr-2 h-4 w-4' />
                  {t('templates.preview.button')}
                </Button>

                <Button
                  type='button'
                  className={primaryButtonClass}
                  onClick={submit}
                  disabled={saving}
                >
                  {t('common:actions.save')}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <LoadingOverlay open={previewing} />

      <ConfirmDeleteDialog
        open={!!toDelete}
        onOpenChange={next => !remove.isPending && !next && setToDelete(null)}
        title={t('templates.dialog.deleteTitle')}
        description={t('common.confirmDelete', { name: toDelete?.name })}
        confirmLabel={t('common:actions.delete')}
        onConfirm={confirmDelete}
        busy={remove.isPending}
      />
    </>
  )
}

export default TemplatePanel
